/**
 * "You sent it — here is the reference", carried across a navigation.
 *
 * ⚠️ IT EXISTS BECAUSE FINISHING A REQUEST NOW LEAVES THE PAGE (Josh,
 * 2026-09-05: "when you finish a request, it takes you back to the Requests
 * Center so you can see the sent request in the dashboard"). The three request
 * pages used to show their confirmation in place, which was fine while each was
 * its own destination. Now that the Requests Center is the destination, sending
 * and then staying put means the new row is on a page you have just been sent
 * away from.
 *
 * ⚠️ AND THE REFERENCE HAS TO SURVIVE THE TRIP. Simply navigating would throw
 * away the one thing worth keeping: LR-4831QA is how a carrier's reply gets
 * routed back, and the loss-run warning about a missing authorization is the
 * most likely reason the request never gets answered. Both ride along in
 * router state and are shown at the top of the list.
 *
 * ⚠️ ROUTER STATE, NOT A QUERY PARAM OR STORAGE. A query string would put a
 * reference into a URL somebody might share, and sessionStorage would resurrect
 * the banner on a later visit. Router state is gone on refresh, which is
 * exactly the lifetime a confirmation should have.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Receipt {
  /** The reference code — LR-, FN-, BR- and so on. */
  id: string;
  /** One sentence: what just happened, in the past tense. */
  headline: string;
  /** Something true and unwelcome about what was just sent, if there is any. */
  warn?: string;
}

/** The second argument to `navigate()` — `navigate(href, receiptState({...}))`. */
export const receiptState = (receipt: Receipt): { state: { receipt: Receipt } } => ({
  state: { receipt },
});

/**
 * Read the receipt this page was navigated to with, once.
 *
 * The history entry is rewritten immediately so a refresh — or a back-and-forward
 * — does not announce a send that happened ten minutes ago.
 */
export function useReceipt(): Receipt | null {
  const location = useLocation();
  const navigate = useNavigate();
  const incoming = (location.state as { receipt?: Receipt } | null)?.receipt ?? null;
  const [receipt, setReceipt] = useState<Receipt | null>(incoming);

  useEffect(() => {
    if (!incoming) return;
    setReceipt(incoming);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // location.state is what changed; re-running on pathname would clear it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  return receipt;
}
